import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  CheckSquare,
  FolderKanban,
  Users,
  Users2,
  Building2,
  Settings,
  BarChart3,
  Calendar,
  Bell,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  CheckCircle2,
  ClipboardList,
  FileText,
  ClipboardCheck,
  TrendingUp,
  MapPin,
} from 'lucide-react';
import { useNotifications } from '@/features/notifications/hooks/useNotifications';
import { usePermissions } from '@/hooks/usePermissions';
import { LucideIcon } from 'lucide-react';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

interface NavChild {
  icon: LucideIcon;
  label: string;
  path: string;
  requiredRoles?: string[];
}

interface NavItem {
  icon: LucideIcon;
  label: string;
  path: string;
  badge?: number;
  requiredRoles?: string[];
  children?: NavChild[];
}

const navItems: NavItem[] = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
  { icon: ClipboardList, label: 'Plano do Dia', path: '/daily-plan' },
  { icon: FileText, label: 'Relatório do Dia', path: '/daily-report' },
  { icon: CheckSquare, label: 'Tarefas', path: '/tasks' },
  { icon: FolderKanban, label: 'Projetos', path: '/projects' },
  { icon: MapPin, label: 'Presenças', path: '/attendance' },
  { icon: Calendar, label: 'Calendário', path: '/calendar' },
  { icon: Users2, label: 'Equipas', path: '/teams' },
  {
    icon: BarChart3,
    label: 'Relatórios',
    path: '/reports',
    requiredRoles: ['admin', 'manager'],
    children: [
      { icon: TrendingUp, label: 'Análises', path: '/reports', requiredRoles: ['admin', 'manager'] },
      { icon: ClipboardCheck, label: 'Mapa de Controle', path: '/control-map', requiredRoles: ['admin', 'manager'] },
    ],
  },
  { icon: Bell, label: 'Notificações', path: '/notifications' },
  { icon: MapPin, label: 'Locais de Trabalho', path: '/work-locations', requiredRoles: ['admin'] },
  { icon: Users, label: 'Utilizadores', path: '/users', requiredRoles: ['admin'] },
  { icon: Building2, label: 'Organizações', path: '/organizations', requiredRoles: ['admin'] },
  { icon: Settings, label: 'Configurações', path: '/settings', requiredRoles: ['admin'] },
];

export function Sidebar({ isOpen, onToggle }: SidebarProps) {
  const { notifications } = useNotifications();
  const { isAdmin, isManager } = usePermissions();
  const location = useLocation();
  const unreadCount = notifications?.filter((n) => !n.isRead).length || 0;

  // Track which parent menus are expanded
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(() => {
    // Auto-expand if current path matches a child
    const initial = new Set<string>();
    navItems.forEach((item) => {
      if (item.children) {
        const childPaths = item.children.map((c) => c.path);
        if (childPaths.includes(location.pathname)) {
          initial.add(item.label);
        }
      }
    });
    return initial;
  });

  const toggleMenu = (label: string) => {
    setExpandedMenus((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  const userRole = isAdmin() ? 'admin' : isManager() ? 'manager' : 'user';

  // Filter nav items based on role and add badges
  const hasAccess = (requiredRoles?: string[]) =>
    !requiredRoles || requiredRoles.includes(userRole);

  const updatedNavItems = navItems
    .filter((item) => hasAccess(item.requiredRoles))
    .map((item) => {
      let updated = item;
      if (item.path === '/notifications') {
        updated = { ...updated, badge: unreadCount };
      }
      // Filter children based on role
      if (updated.children) {
        updated = {
          ...updated,
          children: updated.children.filter((child) => hasAccess(child.requiredRoles)),
        };
      }
      return updated;
    });

  const isChildActive = (item: NavItem) => {
    return item.children?.some((child) => location.pathname === child.path) || false;
  };

  // Render a single nav item (no children)
  const renderNavLink = (
    item: NavItem | NavChild,
    onClick?: () => void,
    indent = false
  ) => {
    const Icon = item.icon;
    return (
      <NavLink
        key={item.path}
        to={item.path}
        onClick={onClick}
        className={({ isActive }) =>
          `flex items-center gap-3 ${indent ? 'pl-9 pr-3' : 'px-3'} py-3 rounded-xl transition-all relative group ${
            isActive
              ? 'bg-black dark:bg-white text-white dark:text-black'
              : 'text-black/60 dark:text-white/60 hover:text-black dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5'
          }`
        }
      >
        {({ isActive }) => (
          <>
            <Icon className="h-5 w-5 flex-shrink-0" strokeWidth={2} />
            {isOpen ? (
              <motion.span
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="text-sm font-medium"
              >
                {item.label}
              </motion.span>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileHover={{ opacity: 1, scale: 1 }}
                className="absolute left-full ml-2 px-3 py-2 bg-black dark:bg-white text-white dark:text-black text-sm font-medium rounded-lg whitespace-nowrap pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"
              >
                {item.label}
              </motion.div>
            )}
            {'badge' in item && item.badge !== undefined && item.badge > 0 && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className={`ml-auto flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold ${
                  isActive
                    ? 'bg-white dark:bg-black text-black dark:text-white'
                    : 'bg-black dark:bg-white text-white dark:text-black'
                }`}
              >
                {item.badge}
              </motion.div>
            )}
          </>
        )}
      </NavLink>
    );
  };

  // Render a parent item with expandable children (desktop)
  const renderExpandableItem = (item: NavItem) => {
    const Icon = item.icon;
    const isExpanded = expandedMenus.has(item.label);
    const hasActiveChild = isChildActive(item);

    return (
      <div key={item.label}>
        <button
          onClick={() => toggleMenu(item.label)}
          className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all relative group ${
            hasActiveChild && !isExpanded
              ? 'bg-black dark:bg-white text-white dark:text-black'
              : 'text-black/60 dark:text-white/60 hover:text-black dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5'
          }`}
        >
          <Icon className="h-5 w-5 flex-shrink-0" strokeWidth={2} />
          {isOpen ? (
            <>
              <motion.span
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="text-sm font-medium flex-1 text-left"
              >
                {item.label}
              </motion.span>
              <ChevronDown
                className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                strokeWidth={2}
              />
            </>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileHover={{ opacity: 1, scale: 1 }}
              className="absolute left-full ml-2 px-3 py-2 bg-black dark:bg-white text-white dark:text-black text-sm font-medium rounded-lg whitespace-nowrap pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"
            >
              {item.label}
            </motion.div>
          )}
        </button>

        {/* Children */}
        <AnimatePresence initial={false}>
          {isExpanded && isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="mt-1 space-y-1">
                {item.children!.map((child) => {
                  const ChildIcon = child.icon;
                  return (
                    <NavLink
                      key={child.path}
                      to={child.path}
                      className={({ isActive }) =>
                        `flex items-center gap-3 pl-9 pr-3 py-2.5 rounded-xl transition-all ${
                          isActive
                            ? 'bg-black dark:bg-white text-white dark:text-black'
                            : 'text-black/60 dark:text-white/60 hover:text-black dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5'
                        }`
                      }
                    >
                      <ChildIcon className="h-4 w-4 flex-shrink-0" strokeWidth={2} />
                      <span className="text-sm font-medium">{child.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <motion.aside
        initial={false}
        animate={{ width: isOpen ? 256 : 80 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="fixed left-0 top-0 h-screen bg-white dark:bg-black border-r border-black/10 dark:border-white/10 z-40 hidden lg:flex flex-col"
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-6 border-b border-black/10 dark:border-white/10">
          <motion.div
            className="flex items-center gap-3 cursor-pointer"
            animate={{ justifyContent: isOpen ? 'flex-start' : 'center' }}
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-black dark:bg-white flex-shrink-0">
              <CheckCircle2 className="h-5 w-5 text-white dark:text-black" strokeWidth={2.5} />
            </div>
            {isOpen && (
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="text-xl font-semibold tracking-tight"
              >
                TaskFlow
              </motion.span>
            )}
          </motion.div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-6 px-3 overflow-y-auto">
          <div className="space-y-1">
            {updatedNavItems.map((item) => {
              if (item.children && item.children.length > 0) {
                return renderExpandableItem(item);
              }
              return renderNavLink(item);
            })}
          </div>
        </nav>

        {/* Toggle Button */}
        <div className="p-3 border-t border-black/10 dark:border-white/10">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onToggle}
            className="w-full flex items-center justify-center gap-2 px-3 py-3 rounded-xl border border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
          >
            {isOpen ? (
              <ChevronLeft className="h-5 w-5" strokeWidth={2} />
            ) : (
              <ChevronRight className="h-5 w-5" strokeWidth={2} />
            )}
          </motion.button>
        </div>
      </motion.aside>

      {/* Mobile Sidebar */}
      <motion.aside
        initial={{ x: -256 }}
        animate={{ x: isOpen ? 0 : -256 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="fixed left-0 top-0 h-screen w-64 bg-white dark:bg-black border-r border-black/10 dark:border-white/10 z-40 lg:hidden flex flex-col"
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-6 border-b border-black/10 dark:border-white/10">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-black dark:bg-white">
              <CheckCircle2 className="h-5 w-5 text-white dark:text-black" strokeWidth={2.5} />
            </div>
            <span className="text-xl font-semibold tracking-tight">TaskFlow</span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-6 px-3 overflow-y-auto">
          <div className="space-y-1">
            {updatedNavItems.map((item) => {
              if (item.children && item.children.length > 0) {
                const isExpanded = expandedMenus.has(item.label);
                const hasActiveChild = isChildActive(item);
                const Icon = item.icon;

                return (
                  <div key={item.label}>
                    <button
                      onClick={() => toggleMenu(item.label)}
                      className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all ${
                        hasActiveChild && !isExpanded
                          ? 'bg-black dark:bg-white text-white dark:text-black'
                          : 'text-black/60 dark:text-white/60 hover:text-black dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5'
                      }`}
                    >
                      <Icon className="h-5 w-5" strokeWidth={2} />
                      <span className="text-sm font-medium flex-1 text-left">{item.label}</span>
                      <ChevronDown
                        className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        strokeWidth={2}
                      />
                    </button>

                    <AnimatePresence initial={false}>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="mt-1 space-y-1">
                            {item.children!.map((child) => {
                              const ChildIcon = child.icon;
                              return (
                                <NavLink
                                  key={child.path}
                                  to={child.path}
                                  onClick={onToggle}
                                  className={({ isActive }) =>
                                    `flex items-center gap-3 pl-9 pr-3 py-2.5 rounded-xl transition-all ${
                                      isActive
                                        ? 'bg-black dark:bg-white text-white dark:text-black'
                                        : 'text-black/60 dark:text-white/60 hover:text-black dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5'
                                    }`
                                  }
                                >
                                  <ChildIcon className="h-4 w-4" strokeWidth={2} />
                                  <span className="text-sm font-medium">{child.label}</span>
                                </NavLink>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              }

              const Icon = item.icon;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={onToggle}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-3 rounded-xl transition-all ${
                      isActive
                        ? 'bg-black dark:bg-white text-white dark:text-black'
                        : 'text-black/60 dark:text-white/60 hover:text-black dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5'
                    }`
                  }
                >
                  <Icon className="h-5 w-5" strokeWidth={2} />
                  <span className="text-sm font-medium">{item.label}</span>
                  {item.badge !== undefined && item.badge > 0 && (
                    <div className="ml-auto flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-black dark:bg-white text-white dark:text-black text-xs font-bold">
                      {item.badge}
                    </div>
                  )}
                </NavLink>
              );
            })}
          </div>
        </nav>
      </motion.aside>
    </>
  );
}
